import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, Loader2, AtSign, Sparkles, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-time username picker. Pops up automatically when the logged-in user
 * either has no `username` yet or has only an auto-generated one (from the
 * legacy backfill migration). Usernames are immutable once confirmed.
 */
const SetUsernameDialog = () => {
  const { user, refreshUser, updateUser, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ available: null, reason: '', suggestion: '' });

  // Open when needed; never reopen if user already confirmed.
  // To avoid showing the modal based on a stale cached `user` snapshot from
  // localStorage (a common case after the username backfill migration), we
  // re-fetch /auth/me ONCE when the cached user looks like it still needs a
  // username, then re-evaluate.
  const [refreshed, setRefreshed] = useState(false);
  useEffect(() => {
    if (!user) return;
    const cachedThinksNeeds = !user.username || user.username_auto_generated;
    if (cachedThinksNeeds && !refreshed) {
      // Refresh once to be sure — cached snapshot may pre-date the backfill.
      setRefreshed(true);
      Promise.resolve(refreshUser?.()).catch(() => {});
      return;
    }
    const needs = !user.username || user.username_auto_generated;
    setOpen(!!needs);
    // Pre-fill the auto-generated username so users see what's been chosen
    // for them and can keep it or replace it. Also trigger an availability
    // check so the "Lock in" button activates immediately if they like it.
    if (needs && user.username) {
      setValue(user.username);
      setTouched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.username, user?.username_auto_generated, refreshed]);

  // Live availability check (debounced). The button stays disabled until
  // the server confirms availability — that guarantee is the whole point
  // of this dialog, so we never let the user submit on a stale check.
  // The token+cache guards in AuthContext now make the auth path
  // reliable; if 401 ever does happen here, the global interceptor will
  // bounce the user to login (handled centrally — see AuthContext).
  useEffect(() => {
    if (!touched || !value) {
      setStatus({ available: null, reason: '', suggestion: '' });
      return;
    }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const { data } = await axios.get(`${API}/users/username/available`, {
          params: { username: value },
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatus(data);
      } catch (err) {
        // 401s are handled by the global interceptor (redirect to login).
        // For everything else (network blip, 5xx), surface the actual
        // server message so the user knows what's wrong instead of a
        // generic "could not check" that gave no diagnostic info.
        const detail = err?.response?.data?.detail || err?.message || 'Could not check availability';
        setStatus({ available: false, reason: detail, suggestion: '' });
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value, touched]);

  const handleSubmit = async () => {
    if (!status.available) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const chosen = value.toLowerCase().trim();
      const { data } = await axios.post(`${API}/users/username`,
        { username: chosen },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Optimistically push the new username into AuthContext + localStorage
      // BEFORE the dialog closes. This stops the dialog from re-opening on
      // the next render when `refreshUser()` silently fails (network blip,
      // race) and the cached `username_auto_generated: true` flag leaks
      // through to the open-condition useEffect.
      const finalUsername = data?.username || chosen;
      updateUser?.({ username: finalUsername, username_auto_generated: false });
      toast.success(`You're now @${finalUsername}`);
      setOpen(false);
      // Fire-and-forget refresh — if it fails, we already have the right
      // state locally; if it succeeds, we just get any other server-side
      // fields the user might have picked up.
      Promise.resolve(refreshUser?.()).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save username');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissible until set */ }}>
      <DialogContent
        className="bg-card border-border/50 sm:max-w-md"
        data-testid="set-username-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-primary" />
            Pick your @username
          </DialogTitle>
          <DialogDescription className="text-left pt-2">
            To keep things private, RollM8s now shows a public username instead of your real name. Pick one — <span className="font-semibold text-foreground">you can&apos;t change it later</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Privacy note */}
          <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 flex gap-2">
            <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Your real name &amp; contact details stay private. They&apos;re only shared with someone you&apos;ve booked a ride with.
            </p>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <div className="relative">
              <AtSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={value}
                onChange={(e) => {
                  setTouched(true);
                  setValue(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''));
                }}
                placeholder="your_username"
                className="pl-9 h-12 rounded-xl text-base"
                maxLength={20}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="username-input"
              />
              {checking && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
              )}
              {!checking && status.available === true && (
                <CheckCircle2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
              )}
              {!checking && status.available === false && (
                <AlertCircle className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
              )}
            </div>

            {touched && status.reason && (
              <p className="text-xs text-red-400 flex items-start gap-1.5">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{status.reason}</span>
                {status.suggestion && (
                  <button
                    type="button"
                    onClick={() => { setValue(status.suggestion); setTouched(true); }}
                    className="underline text-primary hover:text-primary/80 ml-1"
                  >
                    try {status.suggestion}
                  </button>
                )}
              </p>
            )}

            <p className="text-[11px] text-muted-foreground">
              3-20 chars · letters, numbers, . _ - · can&apos;t start or end with a symbol
            </p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!status.available || submitting}
            className="w-full rounded-full"
            data-testid="confirm-username-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Lock in @{value || 'username'}
          </Button>

          {/* Escape hatch — if the user's session is genuinely broken
              (expired token, cache mismatch) they'd otherwise be trapped
              behind this non-dismissible dialog. Logging out clears the
              cache and sends them back to the login screen. */}
          <button
            type="button"
            onClick={() => { logout?.(); }}
            className="w-full text-center text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 pt-1"
            data-testid="username-logout-link"
          >
            Stuck? Log out and try again
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetUsernameDialog;
